/*
 * Project: bellcloud
 * File: packages/types/device/common/system_status_group.ts
 * Author: SWAN
 * Created: 2025-01-27
 * Description: System Status Group Classification (Lite & Mini 공통)
 */

/**
 * System Status 그룹 분류
 * - GroupA: 안전한 상태 (stable)
 * - GroupB: 위험하진 않지만 확인이 필요한 상태 (warning)
 * - GroupC: 우선순위 제일 낮음 (bump test, calibrating, purging 등)
 * - GroupD: 위험한 상태 (fault, alarms 등)
 */
export enum SystemStatusGroup {
  GroupA = 'GroupA', // 안전한 상태
  GroupB = 'GroupB', // 확인이 필요한 상태
  GroupC = 'GroupC', // 우선순위 낮음
  GroupD = 'GroupD', // 위험한 상태
}

/**
 * 각 그룹의 UI 표시 레이블
 */
export const SYSTEM_STATUS_GROUP_DISPLAY_LABEL: Record<SystemStatusGroup, string> = {
  [SystemStatusGroup.GroupA]: 'Stable',
  [SystemStatusGroup.GroupB]: 'Warning',
  [SystemStatusGroup.GroupC]: 'Maintenance',
  [SystemStatusGroup.GroupD]: 'Alarm',
};

/**
 * 상태 레이블을 그룹으로 매핑하는 타입
 */
type StatusLabelToGroupMap = Record<string, SystemStatusGroup>;

/**
 * Lite와 Mini 공통 상태 레이블 -> 그룹 매핑
 */
const COMMON_STATUS_GROUP_MAP: StatusLabelToGroupMap = {
  // GroupA (안전한 상태)
  Normal: SystemStatusGroup.GroupA,
  Stable: SystemStatusGroup.GroupA,

  // GroupB (확인이 필요한 상태)
  Warning: SystemStatusGroup.GroupB,

  // GroupC (우선순위 낮음)
  'Bump Test': SystemStatusGroup.GroupC,
  'Gas Calibrating': SystemStatusGroup.GroupC,
  'Gas Calibration': SystemStatusGroup.GroupC,
  Purging: SystemStatusGroup.GroupC,
  'Low Battery': SystemStatusGroup.GroupC,
  'Invalid Charging Temperature': SystemStatusGroup.GroupC,

  // GroupD (위험한 상태)
  Fault: SystemStatusGroup.GroupD,
  'Fault (error)': SystemStatusGroup.GroupD,
  'Low Alarm': SystemStatusGroup.GroupD,
  'High Alarm': SystemStatusGroup.GroupD,
  'Latched Low Alarm': SystemStatusGroup.GroupD,
  'Latched High Alarm': SystemStatusGroup.GroupD,
  'Over Range': SystemStatusGroup.GroupD,
  'Over-range': SystemStatusGroup.GroupD,
  'STEL Alarm': SystemStatusGroup.GroupD,
  'STEL alarm': SystemStatusGroup.GroupD,
  'TWA Alarm': SystemStatusGroup.GroupD,
  'TWA alarm': SystemStatusGroup.GroupD,
};

/**
 * Lite 전용 상태 레이블 -> 그룹 매핑
 */
export const LITE_STATUS_GROUP_MAP: StatusLabelToGroupMap = {
  ...COMMON_STATUS_GROUP_MAP,
  // GroupA
  'Full Charged': SystemStatusGroup.GroupA,
  Charging: SystemStatusGroup.GroupA,
  'Gas Reading': SystemStatusGroup.GroupA,
};

/**
 * Mini 전용 상태 레이블 -> 그룹 매핑
 */
export const MINI_STATUS_GROUP_MAP: StatusLabelToGroupMap = {
  ...COMMON_STATUS_GROUP_MAP,
  // GroupA
  'Boot up (warm up)': SystemStatusGroup.GroupA,
  'Warm Up': SystemStatusGroup.GroupA,
  'Sensor channel 1 enabled': SystemStatusGroup.GroupA,
};

/**
 * Lite·Mini 전용 레이블을 합친 맵 — 디바이스 타입 분기 없이 동일 우선순위 규칙 적용
 * (전용 키는 서로 겹치지 않으며 COMMON 항목은 동일)
 */
export const UNIFIED_STATUS_GROUP_MAP: StatusLabelToGroupMap = {
  ...LITE_STATUS_GROUP_MAP,
  ...MINI_STATUS_GROUP_MAP,
};

/**
 * 상태 레이블 배열에서 가장 높은 우선순위 그룹 반환
 * 우선순위: GroupD > GroupB > GroupC > GroupA
 * @param statusLabels - 상태 레이블 배열
 * @param statusGroupMap - 상태 레이블 -> 그룹 매핑
 * @returns 가장 높은 우선순위 그룹 (없으면 GroupA)
 */
export function getHighestPriorityGroup(
  statusLabels: string[],
  statusGroupMap: StatusLabelToGroupMap
): SystemStatusGroup {
  if (!statusLabels || statusLabels.length === 0) {
    return SystemStatusGroup.GroupA; // stable
  }

  // 그룹 우선순위: GroupD > GroupB > GroupC > GroupA
  const groupPriority: Record<SystemStatusGroup, number> = {
    [SystemStatusGroup.GroupD]: 4,
    [SystemStatusGroup.GroupB]: 3,
    [SystemStatusGroup.GroupC]: 2,
    [SystemStatusGroup.GroupA]: 1,
  };

  let highestPriority = 0;
  let highestGroup = SystemStatusGroup.GroupA;

  for (const label of statusLabels) {
    const group = statusGroupMap[label] || SystemStatusGroup.GroupA;
    const priority = groupPriority[group];

    if (priority > highestPriority) {
      highestPriority = priority;
      highestGroup = group;
    }
  }

  return highestGroup;
}

/**
 * 상태 레이블 배열에서 특정 그룹에 속하는 상태들 반환
 * @param statusLabels - 상태 레이블 배열
 * @param targetGroup - 찾을 그룹
 * @param statusGroupMap - 상태 레이블 -> 그룹 매핑
 * @returns 해당 그룹에 속하는 상태 레이블 배열
 */
export function getStatusLabelsByGroup(
  statusLabels: string[],
  targetGroup: SystemStatusGroup,
  statusGroupMap: StatusLabelToGroupMap
): string[] {
  if (!statusLabels || statusLabels.length === 0) {
    return [];
  }

  return statusLabels.filter(label => {
    const group = statusGroupMap[label] || SystemStatusGroup.GroupA;
    return group === targetGroup;
  });
}
